import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Modal,
  Popconfirm,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { actionsApi } from 'api/actions'
import {
  IBroadcastAudienceRequest,
  IBroadcastSellers,
  INotificationCampaign,
  TCampaignStatus
} from 'models/notifications'
import { showServerError } from 'shared/utils/showServerError'
import styles from '../Notifications.module.scss'
import { BroadcastSummary } from './BroadcastSummary'
import {
  canCancelCampaign,
  channelNames,
  formatDateTime,
  formatRub,
  getAdminName,
  getCampaignChannels,
  getLinkLabel,
  getMorningWait,
  getSegmentLines,
  isCampaignActive,
  recipientNames,
  statusNames
} from './broadcast'

const PAGE_SIZE = 20
const REFRESH_MS = 3000

const statusColors: Record<TCampaignStatus, string> = {
  scheduled: 'blue',
  queued: 'default',
  running: 'processing',
  completed: 'success',
  failed: 'error',
  canceled: 'default'
}

// Окно не повторяется: оно уже висит в кабинете у продавцов
const getRepeatSellers = (
  sellers?: IBroadcastSellers
): IBroadcastSellers | undefined =>
  sellers && {
    ...sellers,
    popup: false,
    popup_notify_new_sellers: false
  }

const getRepeatRequest = (
  campaign: INotificationCampaign
): IBroadcastAudienceRequest | null => {
  if (!campaign.channels?.length || !campaign.audience) return null
  const sellers = getRepeatSellers(campaign.audience.sellers)
  const users = campaign.audience.users
  const hasChannels =
    sellers?.push ||
    sellers?.bot ||
    sellers?.email ||
    sellers?.sms ||
    users?.push ||
    users?.email
  if (!hasChannels) return null
  return {
    sellers,
    users: users ?? undefined,
    data: campaign.notificationData?.data,
    sms_text: sellers?.sms ? campaign.sms_text ?? undefined : undefined
  }
}

interface CampaignHistoryProps {
  active: boolean
}

export const CampaignHistory: React.FC<CampaignHistoryProps> = ({ active }) => {
  const [modal, modalContextHolder] = Modal.useModal()
  const [items, setItems] = useState<INotificationCampaign[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async (nextPage: number, silent = false) => {
    if (!silent) setIsLoading(true)
    try {
      const response = await actionsApi.getNotificationCampaigns({
        skip: (nextPage - 1) * PAGE_SIZE,
        limit: PAGE_SIZE
      })
      setItems(response.data.items)
      setTotal(response.data.total)
    } catch (e) {
      if (!silent) showServerError(e)
    } finally {
      if (!silent) setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (active) load(page)
  }, [active, page, load])

  // Запланированные тоже: статус меняется сам, когда подходит время
  const hasActive = items.some(
    (campaign) =>
      isCampaignActive(campaign.status) || campaign.status === 'scheduled'
  )

  useEffect(() => {
    if (!active || !hasActive) return
    const timeoutId = window.setTimeout(() => load(page, true), REFRESH_MS)
    return () => window.clearTimeout(timeoutId)
  }, [active, hasActive, items, page, load])

  const repeat = async (campaign: INotificationCampaign) => {
    const request = getRepeatRequest(campaign)
    if (!request) return

    setBusyId(campaign._id)
    let audience
    try {
      const response = await actionsApi.getBroadcastAudience(request)
      audience = response.data
    } catch (e) {
      showServerError(e)
      setBusyId(null)
      return
    }
    setBusyId(null)

    const confirmed = await modal.confirm({
      title: 'Повторить рассылку?',
      width: 560,
      content: (
        <>
          <Typography.Paragraph>
            «{campaign.notificationData?.title}» — тем же каналам и получателям,
            по сегодняшнему списку.
            {campaign.audience?.sellers?.popup &&
              ' Всплывающее окно не повторяется — оно уже показано в кабинете.'}
          </Typography.Paragraph>
          <BroadcastSummary
            audience={audience}
            sellers={request.sellers ?? {}}
            users={request.users ?? {}}
            smsText={request.sms_text}
            linkLabel={getLinkLabel(request.data)}
            quietHours={campaign.quiet_hours}
          />
        </>
      ),
      okText: 'Повторить',
      cancelText: 'Отмена'
    })
    if (!confirmed) return

    setBusyId(campaign._id)
    try {
      await actionsApi.repeatNotificationCampaign(campaign._id)
      message.success('Повтор запущен — он первый в списке')
      if (page === 1) load(1, true)
      else setPage(1)
    } catch (e) {
      showServerError(e)
    } finally {
      setBusyId(null)
    }
  }

  const cancel = async (campaign: INotificationCampaign) => {
    setBusyId(campaign._id)
    try {
      await actionsApi.cancelNotificationCampaign(campaign._id)
      message.success('Рассылка отменена')
      load(page, true)
    } catch (e) {
      showServerError(e)
    } finally {
      setBusyId(null)
    }
  }

  const renderActions = (campaign: INotificationCampaign) => {
    if (canCancelCampaign(campaign.status)) {
      return (
        <div className={styles.historyAction}>
          <Popconfirm
            title='Отменить рассылку?'
            description={
              campaign.status === 'scheduled'
                ? 'Она не будет отправлена'
                : 'Уже отправленное не отзывается — остальным не уйдёт'
            }
            okText='Отменить рассылку'
            cancelText='Нет'
            onConfirm={() => cancel(campaign)}
          >
            <Button size='small' danger loading={busyId === campaign._id}>
              Отменить
            </Button>
          </Popconfirm>
        </div>
      )
    }

    const canRepeat = Boolean(getRepeatRequest(campaign))
    return (
      <div className={styles.historyAction}>
        <Tooltip
          title={
            canRepeat
              ? undefined
              : campaign.channels?.length
              ? 'В рассылке было только всплывающее окно — повторять нечего'
              : 'Эту рассылку отправила прежняя версия админки — повторить её нельзя'
          }
        >
          <Button
            size='small'
            disabled={!canRepeat}
            loading={busyId === campaign._id}
            onClick={() => repeat(campaign)}
          >
            Повторить
          </Button>
        </Tooltip>
      </div>
    )
  }

  const columns: ColumnsType<INotificationCampaign> = [
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      width: 160,
      render: (_, campaign) => (
        <div>
          <div>{formatDateTime(campaign.createdAt)}</div>
          <Typography.Text type='secondary' className={styles.historyMeta}>
            {getAdminName(campaign.created_by)}
          </Typography.Text>
          {campaign.scheduled_at && (
            <Typography.Text type='secondary' className={styles.historyMeta}>
              на {formatDateTime(campaign.scheduled_at)}
            </Typography.Text>
          )}
          <div className={styles.historyStatus}>
            <Tooltip title={campaign.error || undefined}>
              <Tag color={statusColors[campaign.status]}>
                {statusNames[campaign.status] ?? campaign.status}
              </Tag>
            </Tooltip>
            {campaign.repeat_of && <Tag>повтор</Tag>}
            {campaign.dryRun && (
              <Tooltip title='Отправлено с тестового сервера: сообщения только посчитаны'>
                <Tag>тест</Tag>
              </Tooltip>
            )}
          </div>
          {campaign.status === 'canceled' && campaign.canceledAt && (
            <Typography.Text type='secondary' className={styles.historyMeta}>
              {formatDateTime(campaign.canceledAt)},{' '}
              {getAdminName(campaign.canceled_by)}
            </Typography.Text>
          )}
          {renderActions(campaign)}
        </div>
      )
    },
    {
      title: 'Сообщение',
      dataIndex: 'notificationData',
      render: (_, campaign) => (
        <div className={styles.historyMessage}>
          <Typography.Text strong>
            {campaign.notificationData?.title}
          </Typography.Text>
          <Typography.Paragraph
            type='secondary'
            ellipsis={{ rows: 2 }}
            className={styles.historyBody}
          >
            {campaign.notificationData?.body}
          </Typography.Paragraph>
        </div>
      )
    },
    {
      title: 'Кому',
      dataIndex: 'to',
      width: 150,
      render: (_, campaign) => {
        const segments = getSegmentLines(campaign.audience?.users?.segments)
        return (
          <div>
            <div>{recipientNames[campaign.to] ?? campaign.to}</div>
            {campaign.audience?.users?.marketing_only && (
              <Typography.Text type='secondary' className={styles.historyMeta}>
                пользователи — только с согласием
              </Typography.Text>
            )}
            {segments.length > 0 && (
              <Typography.Text type='secondary' className={styles.historyMeta}>
                пользователи: {segments.join(', ')}
              </Typography.Text>
            )}
          </div>
        )
      }
    },
    {
      title: 'Каналы',
      dataIndex: 'channels',
      width: 240,
      render: (_, campaign) => {
        const morningWait = getMorningWait(campaign)
        return (
          <div className={styles.historyChannels}>
            {getCampaignChannels(campaign).map((channel) => (
              <div key={channel.key}>
                {channelNames[channel.key]}:{' '}
                {channel.sent.toLocaleString('ru-RU')} из{' '}
                {channel.total.toLocaleString('ru-RU')}
                {channel.failed ? (
                  <Typography.Text type='danger'>
                    {' '}
                    · ошибок {channel.failed.toLocaleString('ru-RU')}
                  </Typography.Text>
                ) : null}
                {isCampaignActive(channel.status) &&
                  channel.next_at &&
                  dayjs(channel.next_at).isAfter(dayjs()) && (
                    <Typography.Text type='secondary'>
                      {' '}
                      · продолжится{' '}
                      {dayjs(channel.next_at).format('DD.MM в HH:mm')}
                    </Typography.Text>
                  )}
              </div>
            ))}
            {campaign.sms_cost ? (
              <Typography.Text type='secondary'>
                SMS ≈ {formatRub(campaign.sms_cost)}
              </Typography.Text>
            ) : null}
            {morningWait && (
              <Typography.Text type='secondary'>
                Ночные продавцы получат в 9:00 по своему времени
              </Typography.Text>
            )}
          </div>
        )
      }
    }
  ]

  return (
    <>
      {modalContextHolder}
      <Table
        rowKey='_id'
        columns={columns}
        dataSource={items}
        loading={isLoading}
        scroll={{ x: 720 }}
        locale={{ emptyText: 'Рассылок пока не было' }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          onChange: setPage
        }}
      />
    </>
  )
}
